// src/components/ResultsDisplay.js

import React, { useMemo, useState } from "react";
import { useTable, useSortBy } from "react-table";
import ReactPaginate from "react-paginate";
import "./ResultsDisplay.css";
import { InputStyled, PaginationContainerStyled, TabsContainerStyled } from "../styles/Form";
import { TableStyled } from "../styles/Table";
import iconMap from "../data/iconMap";

const logger = {
  info: (msg) => console.info(`INFO: ${msg}`),
  debug: (msg) => console.debug(`DEBUG: ${msg}`),
  warn: (msg) => console.warn(`WARN: ${msg}`),
  error: (msg) => console.error(`ERROR: ${msg}`)
};

const ResultsDisplay = ({
  results = {},
  amortization = [],
  cashFlow = [],
  valuation = {},
  passed_properties = [],
  passed_properties_count = 0,
  total_properties_count = 0,
  annual_irrs = [],
  coc = [],
  isMobile
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const passedListingsColumns = useMemo(
    () => [
      { Header: "List Price ($)", accessor: "list_price", Cell: ({ value }) => `$${parseFloat(value).toLocaleString()}` },
      { Header: "Address", accessor: "address" },
      { Header: "Beds", accessor: "beds" },
      { Header: "Baths", accessor: "baths" },
      { Header: "Sq Ft", accessor: "sqft", Cell: ({ value }) => parseFloat(value).toLocaleString() },
      { Header: "COC (%)", accessor: "coc_percentage_at_year", Cell: ({ value }) => value !== null && value !== undefined ? `${value}%` : "N/A" }
    ],
    []
  );

  const data = useMemo(() => passed_properties, [passed_properties]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    { columns: passedListingsColumns, data },
    useSortBy
  );

  const filteredRows = useMemo(() => {
    if (!searchInput) return rows;
    return rows.filter((row) =>
      Object.values(row.values).some(
        (value) => value && value.toString().toLowerCase().includes(searchInput.toLowerCase())
      )
    );
  }, [rows, searchInput]);

  const pageCount = Math.ceil(filteredRows.length / itemsPerPage);

  const currentPageRows = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const handlePageClick = (selectedObject) => {
    setCurrentPage(selectedObject.selected);
    logger.info(`Page changed to ${selectedObject.selected + 1}`);
  };

  return (
    <TabsContainerStyled>
      {passed_properties.length > 0 ? (
        <>
          <h2>Passed Listings</h2>
          <p>{passed_properties_count}/{total_properties_count} Properties Passed</p>
          <div className="d-flex justify-content-between mb-3">
            <div className="form-group mb-0">
              <InputStyled
                style={{width: '40%', margin: '8px 0px'}}
                type="text"
                placeholder="Search Passed Listings..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setCurrentPage(0);
                  logger.debug(`Search input changed to: ${e.target.value}`);
                }}
              />
            </div>
            <div className="form-group mb-0">
              <select
                className="form-control"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                  logger.debug(`Items per page changed to: ${e.target.value}`);
                }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
          <div className="table-responsive">
            <TableStyled {...getTableProps()}>
              <thead className="thead-dark">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())} scope="col" key={column.id}>
                        {column.render("Header")}
                        <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {currentPageRows.length > 0 ? (
                  currentPageRows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()} key={cell.column.id}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={passedListingsColumns.length} className="text-center">
                      No matched records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </TableStyled>
          </div>
          {pageCount > 1 && (
            <PaginationContainerStyled>
              <ReactPaginate
                previousLabel={iconMap['Back']}
                nextLabel={iconMap['Forward']}
                breakLabel={"..."}
                breakClassName={"page-item"}
                breakLinkClassName={"page-link"}
                pageCount={pageCount}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName={"pagination justify-content-center"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
              />
            </PaginationContainerStyled>
          )}
        </>
      ) : (
        <p>No passed listings available.</p>
      )}

      {amortization && amortization.length > 0 && (
        <>
          <h2>Amortization Schedule</h2>
          <div className="table-responsive">
            <TableStyled>
              <thead className="thead-dark">
                <tr>
                  <th>Year</th>
                  <th>Annual Payment ($)</th>
                  <th>Principal Payment ($)</th>
                  <th>Interest Payment ($)</th>
                  <th>Remaining Balance ($)</th>
                  <th>Cumulative Principal ($)</th>
                  <th>Cumulative Interest ($)</th>
                </tr>
              </thead>
              <tbody>
                {amortization.map((row, index) => (
                  <tr key={index}>
                    <td>{row.Year}</td>
                    <td>${parseFloat(row["Annual Payment"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Principal Payment"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Interest Payment"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Remaining Balance"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Cumulative Principal"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Cumulative Interest"]).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </TableStyled>
          </div>
        </>
      )}

      {cashFlow && cashFlow.length > 0 && (
        <>
          <h2>Cash Flow Analysis</h2>
          <div className="table-responsive">
            <TableStyled>
              <thead className="thead-dark">
                <tr>
                  <th>Year</th>
                  <th>Potential Gross Revenue ($)</th>
                  <th>Vacancy Loss ($)</th>
                  <th>Effective Gross Revenue ($)</th>
                  <th>Property Taxes ($)</th>
                  <th>Operating Expenses ($)</th>
                  <th>CapEx ($)</th>
                  <th>NOI ($)</th>
                  <th>Cash Flow ($)</th>
                </tr>
              </thead>
              <tbody>
                {cashFlow.map((row, index) => (
                  <tr key={index}>
                    <td>{row.Year}</td>
                    <td>${parseFloat(row["Potential Gross Revenue"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Vacancy Loss"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Effective Gross Revenue"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Property Taxes"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Operating Expenses"]).toLocaleString()}</td>
                    <td>${parseFloat(row["CapEx"]).toLocaleString()}</td>
                    <td>${parseFloat(row["NOI"]).toLocaleString()}</td>
                    <td>${parseFloat(row["Cash Flow"]).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </TableStyled>
          </div>
        </>
      )}

      {annual_irrs && annual_irrs.length > 0 && (
        <>
          <h2>Annual Internal Rate of Return (IRR)</h2>
          <div className="table-responsive">
            <TableStyled>
              <thead className="thead-dark">
                <tr>
                  <th>Year</th>
                  <th>IRR (%)</th>
                </tr>
              </thead>
              <tbody>
                {annual_irrs.map((irrData, index) => (
                  <tr key={index}>
                    <td>{irrData.Year}</td>
                    <td>{irrData.IRR !== null ? `${irrData.IRR}%` : "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </TableStyled>
          </div>
        </>
      )}

      {coc && coc.length > 0 && (
        <>
          <h2>Annual Cash on Cash (COC) Return</h2>
          <div className="table-responsive">
            <TableStyled>
              <thead className="thead-dark">
                <tr>
                  <th>Year</th>
                  <th>COC (%)</th>
                </tr>
              </thead>
              <tbody>
                {coc.map((row, index) => (
                  <tr key={index}>
                    <td>{row.Year}</td>
                    <td>{row.COC !== null ? `${row.COC}%` : "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </TableStyled>
          </div>
        </>
      )}

      {valuation && Object.keys(valuation).length > 0 && (
        <>
          <h2>Valuation at Sale</h2>
          <div className="table-responsive">
            <TableStyled>
              <thead className="thead-dark">
                <tr>
                  <th>Sale Price ($)</th>
                  <th>Net Sales Proceeds ($)</th>
                  <th>Cap Rate (%)</th>
                  <th>Final NOI ($)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${parseFloat(valuation["Sale Price"]).toLocaleString()}</td>
                  <td>${parseFloat(valuation["Net Sales Proceeds"]).toLocaleString()}</td>
                  <td>{(parseFloat(valuation["Cap Rate"]) * 100).toFixed(2)}%</td>
                  <td>${parseFloat(valuation["Final NOI"]).toLocaleString()}</td>
                </tr>
              </tbody>
            </TableStyled>
          </div>
        </>
      )}
    </TabsContainerStyled>
  );
};

export default ResultsDisplay;